import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  ReactNode
} from 'react';
import { DashboardCard } from '../dashboard-types';
import { autoScrollSpeed, Box, HitCard, resolveInsertion } from './drag-utils';

// `index` is where the card goes among the target zone's cards with the dragged card removed.
export type DropHandler = (cardId: string, index: number) => void;

export interface DropTarget {
  zoneId: string | null;
  index: number;
  // The insertion line, relative to the zone element's top-left corner.
  marker: Box;
}

export interface DragState {
  card: DashboardCard;
  label: string;
  pointerX: number;
  pointerY: number;
  dropTarget: DropTarget | null;
}

export interface DropZone {
  el: HTMLElement;
  onDrop: DropHandler;
  // The zone's card ids in ARRAY order — the order a drop index counts in. Not DOM order: the grid
  // renders in reading order, which differs from array order wherever two cards are stacked.
  cardIds: string[];
}

export interface DragActions {
  startDrag(
    card: DashboardCard,
    label: string,
    pointerX: number,
    pointerY: number,
    cardEl: HTMLElement
  ): void;
  registerZone(id: string | null, zone: DropZone): void;
  unregisterZone(id: string | null): void;
}

// Two contexts so cards, which only need the stable actions, don't re-render on every pointer move.
const ActionsCtx = createContext<DragActions | null>(null);
const StateCtx = createContext<DragState | null>(null);

export function useDragActions() {
  const ctx = useContext(ActionsCtx);
  if (!ctx) throw new Error('useDragActions must be used inside DragProvider');
  return ctx;
}

export function useDragState() {
  return useContext(StateCtx);
}

// The element that scrolls the card; the document's scroller when no ancestor does.
function findScrollParent(el: HTMLElement): HTMLElement {
  for (let node = el.parentElement; node; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return document.scrollingElement instanceof HTMLElement
    ? document.scrollingElement
    : document.documentElement;
}

const toBox = ({ left, top, right, bottom }: DOMRect): Box => ({ left, top, right, bottom });

const offsetBox = (box: Box, dx: number, dy: number): Box => ({
  left: box.left - dx,
  top: box.top - dy,
  right: box.right - dx,
  bottom: box.bottom - dy
});

// The grid's content box (its border box minus padding), where the full-width markers run.
function contentBox(el: HTMLElement, rect: DOMRect): Box {
  const style = getComputedStyle(el);
  return {
    left: rect.left + parseFloat(style.paddingLeft),
    top: rect.top + parseFloat(style.paddingTop),
    right: rect.right - parseFloat(style.paddingRight),
    bottom: rect.bottom - parseFloat(style.paddingBottom)
  };
}

export const DragProvider = ({ children }: { children: ReactNode }) => {
  const [dragState, setDragState] = useState<DragState | null>(null);
  const stateRef = useRef<DragState | null>(null);
  const zonesRef = useRef<Map<string | null, DropZone>>(new Map());

  const computeDropTarget = useCallback(
    (px: number, py: number, draggedId: string): DropTarget | null => {
      for (const [zoneId, { el, cardIds }] of zonesRef.current.entries()) {
        const rect = el.getBoundingClientRect();
        if (px < rect.left || px > rect.right || py < rect.top || py > rect.bottom) continue;

        const cards: HitCard[] = cardIds.flatMap(id => {
          const cardEl = el.querySelector(`[data-card-id="${CSS.escape(id)}"]`);
          return cardEl ? [{ id, rect: toBox(cardEl.getBoundingClientRect()) }] : [];
        });
        const insertion = resolveInsertion(px, py, cards, draggedId, contentBox(el, rect));
        if (!insertion) return null;
        return {
          zoneId,
          index: insertion.index,
          marker: offsetBox(insertion.marker, rect.left, rect.top)
        };
      }
      return null;
    },
    []
  );

  // Tears down the drag in progress, if any. Kept in a ref so the provider can also end a drag when
  // it unmounts mid-drag.
  const endDragRef = useRef<((commit: boolean) => void) | null>(null);
  useEffect(() => () => endDragRef.current?.(false), []);

  const startDrag = useCallback(
    (
      card: DashboardCard,
      label: string,
      pointerX: number,
      pointerY: number,
      cardEl: HTMLElement
    ) => {
      if (stateRef.current) return;

      const scroller = findScrollParent(cardEl);
      const state: DragState = {
        card,
        label,
        pointerX,
        pointerY,
        dropTarget: computeDropTarget(pointerX, pointerY, card.id)
      };
      stateRef.current = state;
      setDragState(state);
      document.body.style.cursor = 'grabbing';
      document.body.style.userSelect = 'none';
      // Set directly rather than as a prop, so starting a drag doesn't re-render the card.
      cardEl.setAttribute('data-drag-source', '');

      // Scrolling slides the grid under a stationary pointer, so the target is re-resolved on
      // scroll as well as on move.
      const update = (px: number, py: number) => {
        const prev = stateRef.current;
        if (!prev) return;
        const next: DragState = {
          ...prev,
          pointerX: px,
          pointerY: py,
          dropTarget: computeDropTarget(px, py, prev.card.id)
        };
        stateRef.current = next;
        setDragState(next);
      };

      // Auto-scroll waits for the pointer to move, so pressing a handle that happens to sit in an
      // edge band doesn't start scrolling the page on its own.
      let hasMoved = false;
      const onMove = (e: PointerEvent) => {
        hasMoved = hasMoved || e.clientX !== pointerX || e.clientY !== pointerY;
        update(e.clientX, e.clientY);
      };

      const onScroll = () => {
        const s = stateRef.current;
        if (s) {
          update(s.pointerX, s.pointerY);
        }
      };

      // Speed is px/s times elapsed time, so it's the same at any refresh rate; the sub-pixel
      // remainder carries over so slow speeds don't round down to nothing.
      let lastTime = performance.now();
      let carry = 0;
      let frame = requestAnimationFrame(function tick(now) {
        const s = stateRef.current;
        if (!s) return;
        const elapsedSec = Math.min(now - lastTime, 50) / 1000;
        lastTime = now;
        const rect = scroller.getBoundingClientRect();
        const speed = hasMoved
          ? autoScrollSpeed(
              s.pointerY,
              Math.max(rect.top, 0),
              Math.min(rect.bottom, window.innerHeight)
            )
          : 0;
        carry = speed === 0 ? 0 : carry + speed * elapsedSec;
        const step = Math.trunc(carry);
        if (step !== 0) {
          scroller.scrollTop += step;
          carry -= step;
        }
        frame = requestAnimationFrame(tick);
      });

      const endDrag = (commit: boolean) => {
        endDragRef.current = null;
        cancelAnimationFrame(frame);
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        document.removeEventListener('pointercancel', onCancel);
        document.removeEventListener('keydown', onKeyDown);
        document.removeEventListener('scroll', onScroll, true);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        cardEl.removeAttribute('data-drag-source');
        const s = stateRef.current;
        if (commit && s?.dropTarget) {
          const { zoneId, index } = s.dropTarget;
          zonesRef.current.get(zoneId)?.onDrop(s.card.id, index);
        }
        stateRef.current = null;
        setDragState(null);
      };
      // A cancelled gesture (e.g. the browser taking over a touch to scroll) or Escape abandons the
      // drag instead of dropping at the last marker.
      const onUp = () => endDrag(true);
      const onCancel = () => endDrag(false);
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          endDrag(false);
        }
      };
      endDragRef.current = endDrag;

      document.addEventListener('pointermove', onMove);
      document.addEventListener('pointerup', onUp);
      document.addEventListener('pointercancel', onCancel);
      document.addEventListener('keydown', onKeyDown);
      // Capture phase, because element scroll events don't bubble.
      document.addEventListener('scroll', onScroll, true);
    },
    [computeDropTarget]
  );

  const registerZone = useCallback((id: string | null, zone: DropZone) => {
    zonesRef.current.set(id, zone);
  }, []);

  const unregisterZone = useCallback((id: string | null) => {
    zonesRef.current.delete(id);
  }, []);

  const actions = useMemo(
    () => ({ startDrag, registerZone, unregisterZone }),
    [startDrag, registerZone, unregisterZone]
  );

  return (
    <ActionsCtx.Provider value={actions}>
      <StateCtx.Provider value={dragState}>{children}</StateCtx.Provider>
    </ActionsCtx.Provider>
  );
};
